import { Table } from '@app/components/various/Table';
import { BREAKPOINTS } from '@app/constants/breakpoints';
import { useTable } from '@app/utils/hooks/table/useTable';
import { renderStandardCell } from '@app/utils/table';
import { createColumnHelper, getCoreRowModel } from '@tanstack/react-table';
import { Invoice } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { InvoiceItemCard } from './InvoiceItemCard';
import { ProductNameCell } from './ProductNameCell';

interface Props {
  invoice: Invoice.Full;
}

const columnHelper = createColumnHelper<Invoice.InvoiceItem>();

export const InvoiceSummary = ({ invoice }: Props) => {
  const { t } = useTranslation(['common', 'invoices', 'totals']);
  const { invoiceItems, subtotal } = invoice;
  const quantitySum = invoiceItems.reduce((acc, cur) => acc + cur.quantity, 0);

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('name', {
        cell: info => <ProductNameCell invoiceItem={info.row.original} />,
        footer: t('common:summary'),
        header: t('common:name'),
        meta: {
          headerCellClassName: 'heading-3 text-neutral-20 -ml-3',
          isCollapsible: true,
          width: '100%',
        },
      }),
      columnHelper.accessor('quantity', {
        cell: renderStandardCell,
        footer: quantitySum.toString(),
        header: t('common:quantity'),
      }),
      columnHelper.accessor('unitPrice', {
        cell: renderStandardCell,
        header: t('invoices:units_price'),
        meta: {
          align: 'right',
        },
      }),
      columnHelper.accessor('subtotal', {
        cell: renderStandardCell,
        footer: subtotal,
        header: t('totals:subtotal'),
        meta: {
          align: 'right',
          footerCellColspan: 2,
        },
      }),
    ],
    [t, subtotal, quantitySum],
  );

  const tableData: Invoice.InvoiceItem[] = React.useMemo(() => {
    return invoice.invoiceItems.map(item => ({
      ...item,
      key: item.name,
    }));
  }, [invoice]);

  const table = useTable({
    columns,
    data: tableData,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <section>
      <h3 className="sr-only">{t('common:item_other')}</h3>
      <Table
        dataTestId="invoice-summary"
        table={table}
        rowHeight={66}
        mobileBreakpoint={BREAKPOINTS.SM}
        renderCard={(row, index) => <InvoiceItemCard key={index} row={row} />}
      />
    </section>
  );
};
